import { useQuery } from "@tanstack/react-query";
import SiteActions from "../../actions/SiteActions";
import UIActions from "../../actions/UIActions";
import UserActions from "../../actions/UserActions";
import config from "../../config/app";
import { USER_SITES } from "../../constants/QueryKeys";
import SiteFolderConstants from "../../constants/SiteFolderDefaultConstants";
import APIError from "../../service/APIError";
import UserService from "../../service/UserService";
import { queryClient, STORE_CACHE_DEFAULTS } from "../../utils/QueryUtils";

/**
 * @typedef {import("@/types/data/UserSite.mjs").UserSite} UserSite
 * @typedef {import("@/types/data/UserSite.mjs").SiteDesignsFolder} SiteDesignsFolder
 * @typedef {{ sites: UserSite[] }} UserSitesData
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryResultRest<T>} QueryResultRest
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryHookOptions<T>} QueryHookOptions
 */

const { ALL_SITE_DESIGNS_FOLDER_ID, UNCATEGORIZED_FOLDER_ID, UNCATEGORIZED_FOLDER_LABEL } =
  SiteFolderConstants;

const defaults = STORE_CACHE_DEFAULTS;

queryClient.setQueryDefaults([USER_SITES], defaults);

const SIMPLE_DK = "pwpnkxaisvaonv_0pjpmig";
const BLANK_DK = "aal8aleitsgs0ybpgdtuqa";

/** Key + fetcher for USER_SITES; imperative callers spread this into fetchQuery. */
export const userSitesQuery = {
  queryKey: [USER_SITES],
  queryFn: () => /** @type {Promise<UserSitesData>} */ (UserService.loadSites()),
};

/**
 * Resolves the chooser's folder selection (or a direct folderId) to a concrete filter.
 * @param {number|string|undefined} folderId
 * @param {number|string|undefined} selectedFolder
 * @param {Array<{folderId: number, name: string}>} folders
 * @returns {{ folderId: number|string|undefined, isUncategorized: boolean }}
 */
const resolveFolderFilter = (folderId, selectedFolder, folders) => {
  const uncategorized = folders.find((folder) => folder.name === UNCATEGORIZED_FOLDER_LABEL);

  if (selectedFolder !== undefined) {
    if (selectedFolder === ALL_SITE_DESIGNS_FOLDER_ID) {
      folderId = undefined;
    } else if (selectedFolder === UNCATEGORIZED_FOLDER_ID) {
      folderId = uncategorized?.folderId ?? UNCATEGORIZED_FOLDER_ID;
    } else {
      folderId = selectedFolder;
    }
  }

  return {
    folderId,
    isUncategorized:
      folderId === UNCATEGORIZED_FOLDER_ID ||
      (!!uncategorized && folderId === uncategorized.folderId),
  };
};

/**
 * The user's site designs, filtered to a folder when one is selected.
 * @param {QueryHookOptions<UserSitesData> & {
 *   folderId?: number,
 *   selectedFolder?: number|string,
 *   folders?: SiteDesignsFolder[],
 * }} [options] TanStack Query options, plus `folderId` (filter to this folder), `selectedFolder`
 *   (chooser selection; ALL -> none, UNCATEGORIZED -> the uncategorized folder) and `folders`
 *   (used to resolve UNCATEGORIZED).
 * @returns {{ sites: UserSite[] | undefined } & QueryResultRest<UserSitesData>}
 */
const useSites = (options = {}) => {
  const { folderId: folderIdProp, selectedFolder, folders = [], ...queryOptions } = options;
  const { data, ...rest } = useQuery({
    ...userSitesQuery,
    ...defaults,
    ...queryOptions,
  });

  const isDesignShowitCom = window.location.href.includes("design.showit.com");
  let sites =
    config.demoMode && isDesignShowitCom
      ? data?.sites.filter((site) => site.key === SIMPLE_DK || site.key === BLANK_DK)
      : data?.sites;

  const { folderId, isUncategorized } = resolveFolderFilter(folderIdProp, selectedFolder, folders);

  if (folderId && sites) {
    // Uncategorized also includes sites pointing at a deleted folder.
    sites = isUncategorized
      ? sites.filter(
          (site) =>
            !site.folder_id ||
            site.folder_id === folderId ||
            !folders.some((folder) => folder.folderId === site.folder_id),
        )
      : sites.filter((site) => site.folder_id === folderId);
  }

  return {
    sites,
    ...rest,
  };
};

export const invalidateUserSites = () => queryClient.invalidateQueries({ queryKey: [USER_SITES] });

/**
 * Appends a newly created site to the cache.
 * @param {UserSite} site
 */
export const addSiteToCache = (site) =>
  queryClient.setQueryData([USER_SITES], (/** @type {UserSitesData | undefined} */ prev) => ({
    ...prev,
    sites: [...(prev?.sites ?? []), site],
  }));

// ---- Copy/delete site cache + UX seams.

/**
 * `UserActions.copySite.completed` seam.
 * @param {{site: UserSite}} data
 */
export const handleCopySiteComplete = (data) => {
  addSiteToCache(data.site);
  UIActions.showDesignView();
  SiteActions.loadSite(data.site);
};

/**
 * `UserActions.copySite.failed` seam.
 * @param {Error} err
 */
export const handleCopySiteFail = (err) => {
  APIError.handle(err);
};

/**
 * `UserActions.deleteSite.failed` seam.
 * @param {Error} err
 */
export const handleDeleteSiteFail = async (err) => {
  await queryClient.refetchQueries({ queryKey: [USER_SITES] });
  alert(`Failed to delete site. \n ${err.error?.userMessage}`);
  APIError.handle(err);
};

/**
 * `SiteActions.loadSite` seam: keeps the
 * chooser's "Recent" sort honest for boot/palette loads, not just chooser clicks.
 * @param {{site_design_id: number}} data
 */
export const handleLoadSite = ({ site_design_id }) => {
  queryClient.setQueryData([USER_SITES], (/** @type {UserSitesData | undefined} */ prev) => {
    // Bail without a new reference when the site isn't cached -- a write would
    // re-render every useSites consumer for nothing.
    if (!prev?.sites?.some((site) => site.site_design_id === site_design_id)) return prev;
    return {
      ...prev,
      sites: prev.sites.map((site) =>
        site.site_design_id === site_design_id
          ? { ...site, last_access: new Date().toISOString() }
          : site,
      ),
    };
  });
};

SiteActions.loadSite.listen(handleLoadSite);
UserActions.copySite.completed.listen(handleCopySiteComplete);
UserActions.copySite.failed.listen(handleCopySiteFail);
UserActions.deleteSite.failed.listen(handleDeleteSiteFail);

export default useSites;
